import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import ClientPicker from "@/components/ClientPicker";
import DocumentIdInput, { emptyDocSource } from "@/components/DocumentIdInput";
import { typeMeta } from "./FileTypeBadge";

const INPUT = "w-full bg-[#050505] border border-white/15 rounded px-3 py-2 placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-white/30";

function Field({ label, children, hint }) {
    return (
        <label className="block">
            <div className="text-white/40 mb-1">{label}</div>
            {children}
            {hint && <div className="text-[10px] text-white/30 mt-1">{hint}</div>}
        </label>
    );
}

/** Type picker — one pill per file type, the chosen one filled. */
function TypePills({ types, value, onChange }) {
    return (
        <div className="flex flex-wrap gap-1.5" data-testid="ingest-file-type-pills">
            {types.map((t) => {
                const m = typeMeta(t.id);
                const Icon = m.icon;
                const on = value === t.id;
                return (
                    <button key={t.id} type="button" onClick={() => onChange(t.id)} title={t.description} data-testid={`ingest-file-type-${t.id}`} data-on={on ? "true" : "false"}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] transition-colors ${on ? m.tone + " font-medium" : "border-white/15 text-white/55 hover:border-white/30 hover:text-white"}`}>
                        <Icon className="w-3 h-3" strokeWidth={1.75} />{t.label}
                    </button>
                );
            })}
        </div>
    );
}

/** Upload one file of a chosen type — client, study, version label, notes; EDC Guidelines also take a vendor hint. */
export default function IngestFileModal({ meta, defaultType = "", onClose, onDone }) {
    const types = meta?.types || [];
    const [fileType, setFileType] = useState(defaultType);
    const [source, setSource] = useState(emptyDocSource("file"));
    const [clientId, setClientId] = useState("");
    const [studyId, setStudyId] = useState("");
    const [protocolNumber, setProtocolNumber] = useState("");
    const [versionLabel, setVersionLabel] = useState("");
    const [edcHint, setEdcHint] = useState("");
    const [title, setTitle] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const type = types.find((t) => t.id === fileType);
    const accept = (meta?.accept || [".pdf", ".docx"]).join(",");

    useEffect(() => { setFileType(defaultType); }, [defaultType]);

    const ready = fileType && clientId.trim() && (source.mode === "document_id" ? (source.documentId || "").trim() : source.file);

    const submit = async (e) => {
        e?.preventDefault();
        if (!ready) { toast.error("Pick a client and a file"); return; }
        setSubmitting(true);
        try {
            const fd = new FormData();
            if (source.mode === "document_id") fd.append("document_id", source.documentId.trim());
            else fd.append("file", source.file);
            fd.append("file_type", fileType);
            fd.append("client_id", clientId.trim());
            if (studyId.trim()) fd.append("study_id", studyId.trim());
            if (protocolNumber.trim()) fd.append("protocol_number", protocolNumber.trim());
            if (versionLabel.trim()) fd.append("version_label", versionLabel.trim());
            if (title.trim()) fd.append("title", title.trim());
            if (notes.trim()) fd.append("notes", notes.trim());
            if (fileType === "edc_guidelines" && edcHint.trim()) fd.append("edc_system_hint", edcHint.trim());
            const { data } = await api.post("/admin/ingest-files", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
            toast.success(data.reused ? "Already ingested — same file bytes, nothing re-processed."
                : `${data.file_type_label} ${data.version_label} ingested${data.framework_id ? ` · ${data.family_count} families · ${data.field_count} fields` : ""}`);
            onDone(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail || err?.message));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4" data-testid="ingest-file-modal">
            <div className="w-full max-w-xl rounded-md border border-white/15 bg-[#0a0a0a] max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0a0a0a]">
                    <div className="flex items-center gap-2">
                        <Upload className="w-4 h-4 text-white/70" strokeWidth={1.75} />
                        <h2 className="text-sm font-medium">Ingest a file</h2>
                    </div>
                    <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="ingest-file-modal-close"><X className="w-4 h-4 text-white/60" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4 text-xs">
                    <div>
                        <div className="text-white/40 mb-1.5">Type of file *</div>
                        <TypePills types={types} value={fileType} onChange={setFileType} />
                        {type && <div className="text-[11px] text-white/45 mt-1.5" data-testid="ingest-file-type-description">{type.description}</div>}
                    </div>
                    <DocumentIdInput value={source} onChange={setSource} accept={accept} testId="ingest-file" label="File (PDF, DOCX, XLSX, TXT, image · ≤ 100 MB)" />
                    <ClientPicker value={clientId} onChange={setClientId} label="Client *" includeSuperdomain testId="ingest-file-client" />
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Study ID" hint="Versions of the same study and type chain under it">
                            <input type="text" value={studyId} onChange={(e) => setStudyId(e.target.value)} placeholder="STUDY-XYZ-001" data-testid="ingest-file-study-id" className={`${INPUT} font-mono`} />
                        </Field>
                        <Field label="Protocol number" hint={fileType === "edc_guidelines" ? "Hint for the extraction; chain key when Study ID is empty" : "Chain key when Study ID is empty"}>
                            <input type="text" value={protocolNumber} onChange={(e) => setProtocolNumber(e.target.value)} placeholder="ACME-2026-001" data-testid="ingest-file-protocol-number" className={`${INPUT} font-mono`} />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Version label" hint="Blank: the document's own version when found, else v{N+1}">
                            <input type="text" value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} placeholder="v2 / 2026Q1" data-testid="ingest-file-version-label" className={`${INPUT} font-mono`} />
                        </Field>
                        {fileType === "edc_guidelines" ? (
                            <Field label="EDC vendor (hint)">
                                <input type="text" value={edcHint} onChange={(e) => setEdcHint(e.target.value)} placeholder="Zelta / Medidata / Veeva / Castor" data-testid="ingest-file-edc-hint" className={INPUT} />
                            </Field>
                        ) : (
                            <Field label="Title" hint="Defaults to the file name">
                                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Central Lab Manual" data-testid="ingest-file-title" className={INPUT} />
                            </Field>
                        )}
                    </div>
                    <Field label="Notes">
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="ingest-file-notes" className={INPUT} />
                    </Field>
                    <div className="flex items-start gap-2 p-2 rounded border border-white/10 bg-white/[0.03] text-white/55">
                        <Sparkles className="w-3 h-3 mt-0.5 shrink-0 text-[#FFB000]" strokeWidth={1.75} />
                        <span>
                            {!fileType
                                ? <>Pick the type first — it decides what happens to the file after the text is extracted.</>
                                : fileType === "edc_guidelines"
                                ? <>The form families and fields are extracted through the <Link to="/flows" className="underline">edc-guidelines-ingestion</Link> flow and feed <Link to="/qa/form-generation" className="underline">Form Generation</Link>.</>
                                : <>The text is extracted, Claude writes a summary with key topics and the file lands in the <Link to="/datalake" className="underline">Data Lake</Link> under the <span className="font-mono">{type?.context}</span> context.</>}
                            {" "}Re-ingesting for the same client, study and type creates a new version; the previous one stays.
                        </span>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-1">
                        <button type="button" onClick={onClose} className="px-3 py-1.5 rounded text-white/60 hover:text-white" data-testid="ingest-file-cancel">Cancel</button>
                        <button type="submit" disabled={submitting || !ready} data-testid="ingest-file-submit"
                            className="flex items-center gap-1.5 px-4 py-2 rounded bg-white text-black font-medium hover:bg-gray-200 disabled:opacity-40">
                            <Upload className="w-3.5 h-3.5" strokeWidth={1.75} />
                            {submitting ? "Ingesting…" : "Ingest"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
